import React from 'react';
import { TabIndex } from '../navigation';
import LinkIcon from '@mui/icons-material/Link';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useAuth } from '../contexts/AuthContext';
import { useMFA } from '../contexts/MFAContext';
import { useApi } from '../contexts/ApiContext';
import { useWithTokenRefresh } from '../utils/api';
import { useQuota } from '../contexts/QuotaContext';
import { SystemEventTypes } from '@flashbacktech/flashbackclient/api';
import { usePermissions } from '../hooks/usePermissions';
import { WorkspaceStatsService } from '../services/WorkspaceStatsService';
import { useNavigate, useLocation } from 'react-router-dom';
import ProfileModal from './ProfileModal';
import OrganizationModal from './OrganizationModal';
import UpgradePlanModal from './UpgradePlanModal';

// Import des icônes SVG pour la sidebar
import homeSvg from '../assets/icons-home/home.svg';
import cloudSvg from '../assets/icons-home/cloud.svg';
import docsSvg from '../assets/icons-home/docs.svg';

// Import des icônes de notifications
import checkNotifsIcon from '../assets/notifications/check-notifs.svg';
import reloadNotifsIcon from '../assets/notifications/reload-notifs.svg';
import warningNotifsIcon from '../assets/notifications/warning-notifs.svg';
import supportSvg from '../assets/icons-home/support.svg';
import bellSvg from '../assets/icons-home/bell.svg';
import docsLinkSvg from '../assets/sidebar-icons/docs-link.svg';
import greyCheckSvg from '../assets/sidebar-icons/grey-check.svg';
import blueCheckSvg from '../assets/sidebar-icons/blue-check.svg';
import ArchiveIcon from '@mui/icons-material/Archive';
import StorefrontIcon from '@mui/icons-material/Storefront';
import StorageIcon from '@mui/icons-material/Storage';
import HomeIcon from '@mui/icons-material/Home';
import GroupsIcon from '@mui/icons-material/Groups';
import FolderIcon from '@mui/icons-material/Folder';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import PaymentIcon from '@mui/icons-material/Payment';
import GavelIcon from '@mui/icons-material/Gavel';
import BarChartIcon from '@mui/icons-material/BarChart';
import HandshakeIcon from '@mui/icons-material/Handshake';
import { getPlanTier, getPlanColor } from '../utils/subscriptionUtils';

// Logo X SVG component
const XLogo = ({ size = 20, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" className={className}>
    <path
      fill="currentColor"
      d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
    />
  </svg>
);

// Codesandbox Icon component (for Bridge Nodes)
const CodesandboxIcon = ({
  size = 18,
  className = '',
  color = '#98A2B3',
}: {
  size?: number;
  className?: string;
  color?: string;
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="7.5 4.21 12 6.81 16.5 4.21" />
    <polyline points="7.5 19.79 7.5 14.6 3 12" />
    <polyline points="21 12 16.5 14.6 16.5 19.79" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" x2="12" y1="22.08" y2="12" />
  </svg>
);

// Hub Icon component (central dashboard icon)
const HubIcon = ({
  size = 18,
  className = '',
  color = '#98A2B3',
}: {
  size?: number;
  className?: string;
  color?: string;
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
  </svg>
);

// ISP Market Icon component
const DepinHubIcon = ({
  size = 18,
  className = '',
  color = '#98A2B3',
}: {
  size?: number;
  className?: string;
  color?: string;
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="12" cy="12" r="3" />
    <circle cx="5" cy="5" r="2" />
    <circle cx="19" cy="5" r="2" />
    <circle cx="5" cy="19" r="2" />
    <circle cx="19" cy="19" r="2" />
    <line x1="12" y1="9" x2="5" y2="7" />
    <line x1="12" y1="9" x2="19" y2="7" />
    <line x1="12" y1="15" x2="5" y2="17" />
    <line x1="12" y1="15" x2="19" y2="17" />
  </svg>
);

// AI Icon component
const AIIcon = ({
  size = 18,
  className = '',
  color = '#98A2B3',
}: {
  size?: number;
  className?: string;
  color?: string;
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M12 2L2 7l10 5 10-5-10-5z" />
    <path d="M2 17l10 5 10-5" />
    <path d="M2 12l10 5 10-5" />
  </svg>
);

// Repositories Icon component
const RepositoriesIcon = ({
  size = 18,
  className = '',
  color = '#98A2B3',
}: {
  size?: number;
  className?: string;
  color?: string;
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);

// Statistics Icon component
const StatisticsIcon = ({
  size = 18,
  className = '',
  color = '#98A2B3',
}: {
  size?: number;
  className?: string;
  color?: string;
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

interface TabItem {
  icon: React.ReactNode;
  label: string;
  subItems?: SubItem[];
  navigateTo?: number;
  hidden?: boolean;
  comingSoon?: boolean;
}

interface SubItem {
  label: string;
  navigateTo: number;
  icon?: React.ReactNode;
}

interface SidebarProps {
  tabValue: number;
  handleTabChange: (event: React.SyntheticEvent, newValue: number) => void;
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (isOpen: boolean) => void;
  sidebarCollapsed?: boolean;
  onSidebarCollapse?: (collapsed: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  tabValue,
  handleTabChange,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  sidebarCollapsed: sidebarCollapsedProp,
  onSidebarCollapse,
}) => {
  const [expandedTabs, setExpandedTabs] = React.useState<number[]>([]);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [upgradePlanModalOpen, setUpgradePlanModalOpen] = React.useState(false);
  const [sidebarCollapsedInternal, setSidebarCollapsedInternal] = React.useState(false);
  const sidebarCollapsed = sidebarCollapsedProp ?? sidebarCollapsedInternal;

  // Notify parent when internal state changes
  React.useEffect(() => {
    if (sidebarCollapsedProp === undefined) {
      onSidebarCollapse?.(sidebarCollapsedInternal);
    }
  }, [sidebarCollapsedInternal, sidebarCollapsedProp, onSidebarCollapse]);

  // Écouter les changements de taille d'écran
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [profileModalOpen, setProfileModalOpen] = React.useState(false);
  const [profileModalInitialSection, setProfileModalInitialSection] = React.useState<
    'Profile' | 'Organization' | 'Plan' | 'Billing' | 'Wallet' | 'Referrals'
  >('Profile');
  const [profileModalInitialTab, setProfileModalInitialTab] = React.useState<
    'Personal' | 'Notifications' | 'Security' | undefined
  >(undefined);
  const [profileModalHighlight2FA, setProfileModalHighlight2FA] = React.useState(false);
  const [usedSpaceCollapsed, setUsedSpaceCollapsed] = React.useState(true);
  const [bucketsCount, setBucketsCount] = React.useState(() => {
    // Charger depuis le cache au montage
    const workspaceId = localStorage.getItem('selectedWorkspaceId') || '';
    if (workspaceId) {
      const cached = WorkspaceStatsService.getStatsFromCache(workspaceId);
      if (cached) return cached.bucketsCount;
    }
    return 0;
  });
  const [repositoriesCount, setRepositoriesCount] = React.useState(() => {
    // Charger depuis le cache au montage
    const workspaceId = localStorage.getItem('selectedWorkspaceId') || '';
    if (workspaceId) {
      const cached = WorkspaceStatsService.getStatsFromCache(workspaceId);
      if (cached) return cached.reposCount;
    }
    return 0;
  });
  const [privateNodesCount, setPrivateNodesCount] = React.useState(0);
  const [publicNodesCount, setPublicNodesCount] = React.useState(0);
  const [hasRepoWithApiKey, setHasRepoWithApiKey] = React.useState(() => {
    // Charger depuis le cache WorkspaceStatsService
    const workspaceId = localStorage.getItem('selectedWorkspaceId') || '';
    if (workspaceId) {
      const cached = WorkspaceStatsService.getStatsFromCache(workspaceId);
      if (cached) return cached.hasRepoWithApiKey;
    }
    // Fallback sur localStorage ancien système
    try {
      const cached = localStorage.getItem('hasRepoWithApiKey');
      return cached === 'true';
    } catch {
      return false;
    }
  });
  const [hasUploadedData, setHasUploadedData] = React.useState(() => {
    // Charger depuis le cache WorkspaceStatsService
    const workspaceId = localStorage.getItem('selectedWorkspaceId') || '';
    if (workspaceId) {
      const cached = WorkspaceStatsService.getStatsFromCache(workspaceId);
      if (cached) return cached.hasUploadedData;
    }
    // Fallback sur localStorage ancien système
    try {
      const cached = localStorage.getItem('hasUploadedData');
      return cached === 'true';
    } catch {
      return false;
    }
  });
  const [orgId, setOrgId] = React.useState<string>('');
  // Workspace selector moved to Overview page
  const open = Boolean(anchorEl);

  const { authState, handleSignOut, globalData, setSelectedWorkspace } =
    useAuth();
  const { apiClient } = useApi();
  const withTokenRefresh = useWithTokenRefresh();
  const { isOrgManager } = usePermissions();
  const currentUserId = globalData?.userProfile?.id || '';
  const navigate = useNavigate();
  const location = useLocation();
  const { mfaState } = useMFA();
  const { quotaData: quotaContextData, refreshQuota } = useQuota();
  
  // Initialize WorkspaceStatsService with apiClient and withTokenRefresh
  React.useEffect(() => {
    if (apiClient) {
      WorkspaceStatsService.initialize(apiClient, withTokenRefresh);
    }
  }, [apiClient, withTokenRefresh]);
  
  // Check if we're on special pages where header is full-width
  const isOnOrganizationPage = location.pathname.startsWith('/organization');
  const isOnWorkspacePage = location.pathname.startsWith('/workspace');
  const isOnChatPage = location.pathname.startsWith('/chat');
  const isOnDePINHubPage = location.pathname.startsWith('/depin-hub');
  
  // Pages where header should be full width (over sidebar)
  const isFullWidthHeaderPage = isOnOrganizationPage || isOnWorkspacePage;
  
  // Get the current organization section from URL params
  const getActiveOrgSection = (): string => {
    if (!isOnOrganizationPage) return '';
    const params = new URLSearchParams(location.search);
    const tabParam = (params.get('tab') || '').toLowerCase();
    // Map URL params to section labels
    const mapping: Record<string, string> = {
      '': 'Home',
      'overview': 'Home',
      'team': 'Team',
      'workspace': 'Workspace',
      'billing': 'Billing',
      'activation-links': 'Activation Links',
      'policies': 'Policies',
      'statistics': 'Statistics',
    };
    return mapping[tabParam] || 'Home';
  };
  const activeOrgSection = getActiveOrgSection();

  // Get active org sub-section from URL
  const getActiveOrgSubSection = (): string => {
    if (!isOnOrganizationPage) return '';
    const params = new URLSearchParams(location.search);
    return (params.get('section') || '').toLowerCase();
  };
  const activeOrgSubSection = getActiveOrgSubSection();

  // Use QuotaContext instead of local state and API calls
  // Map quotaContextData to the format expected by Sidebar
  const quotaData = React.useMemo(() => {
    if (quotaContextData.quotaUsage) {
      return quotaContextData.quotaUsage;
    }
    // Fallback to empty values if not loaded yet
    return {
      storageUsed: 0,
      storageLimit: 0,
      storagePercentage: 0,
      subscriptionName: '',
      subscriptionType: '',
      displayLimit: '',
    };
  }, [quotaContextData.quotaUsage]);

  // Fonction centralisée pour charger les stats via WorkspaceStatsService
  const loadWorkspaceStats = React.useCallback(async (forceRefresh = false) => {
    try {
      if (!authState?.token || !globalData?.isReady) return;
      
      const workspaceId = localStorage.getItem('selectedWorkspaceId') || globalData.selectedWorkspaceId || '';
      // CRITICAL: Guard against undefined/invalid workspaceId
      if (!workspaceId || workspaceId === 'undefined' || workspaceId === '' || workspaceId === 'ALL') {
        console.warn('[Sidebar] Invalid workspaceId, skipping stats load:', workspaceId);
        return;
      }
      
      const stats = await WorkspaceStatsService.loadWorkspaceStats(
        workspaceId,
        forceRefresh
      );
      
      setBucketsCount(stats.bucketsCount);
      setRepositoriesCount(stats.reposCount);
      setHasUploadedData(stats.hasUploadedData);
      setHasRepoWithApiKey(stats.hasRepoWithApiKey);
      
    } catch (error) {
      console.error('[Sidebar] Error loading workspace stats:', error);
    }
  }, [authState?.token, globalData?.isReady, globalData.selectedWorkspaceId]);

  // Effet pour charger les données de quota, buckets et repositories
  // Wait for globalData.isReady to ensure workspace is selected
  // Note: Quota is now loaded by QuotaContext, WorkspaceStatsService handles buckets/repos
  React.useEffect(() => {
    if (authState.token && globalData?.isReady) {
      // Only use WorkspaceStatsService - it handles buckets/repos/violations in one call
      // Removed loadBucketRepoStats() as it duplicates WorkspaceStatsService
      loadWorkspaceStats();
      loadPublicNodesCount();
    }
  }, [authState.token, globalData?.isReady, loadWorkspaceStats]);

  // Écouter les mises à jour du service centralisé
  React.useEffect(() => {
    const unsubscribe = WorkspaceStatsService.onStatsUpdate((stats) => {
      setBucketsCount(stats.bucketsCount);
      setRepositoriesCount(stats.reposCount);
      setHasUploadedData(stats.hasUploadedData);
      setHasRepoWithApiKey(stats.hasRepoWithApiKey);
    });
    
    return () => unsubscribe();
  }, []);

  // Écouter les changements de workspace
  React.useEffect(() => {
    const handleWorkspaceChange = () => {
      // Invalider le cache et recharger
      const workspaceId = localStorage.getItem('selectedWorkspaceId') || '';
      if (workspaceId) {
        WorkspaceStatsService.invalidateCache(workspaceId);
        loadWorkspaceStats(true);
      }
    };
    
    window.addEventListener('workspaceChanged', handleWorkspaceChange);
    window.addEventListener('bucketsUpdated', handleWorkspaceChange);
    
    return () => {
      window.removeEventListener('workspaceChanged', handleWorkspaceChange);
      window.removeEventListener('bucketsUpdated', handleWorkspaceChange);
    };
  }, [loadWorkspaceStats]);

  // REMOVED: loadBucketRepoStats() - This function duplicated WorkspaceStatsService
  // WorkspaceStatsService.loadWorkspaceStats() already handles buckets/repos/violations
  // Use loadWorkspaceStats() instead, which uses WorkspaceStatsService

  // REMOVED: loadBucketsCount() - This function is redundant
  // WorkspaceStatsService.loadWorkspaceStats() already provides bucketsCount
  // The stats are updated via WorkspaceStatsService.onStatsUpdate() listener (line 444-453)

  // REMOVED: loadRepositoriesCount() - This function is redundant
  // WorkspaceStatsService.loadWorkspaceStats() already provides reposCount and hasRepoWithApiKey
  // The stats are updated via WorkspaceStatsService.onStatsUpdate() listener (line 444-453)

  const loadPrivateNodesCount = async () => {
    try {
      if (!orgId) {
        setPrivateNodesCount(0);
        return;
      }
      const res = await withTokenRefresh(() => apiClient.getPrivateNodeInfo(orgId));
      if (res.success && Array.isArray(res.data)) {
        setPrivateNodesCount(res.data.length);
      } else {
        setPrivateNodesCount(0);
      }
    } catch (error) {
      setPrivateNodesCount(0);
    }
  };

  const loadPublicNodesCount = async () => {
    try {
      const response = await withTokenRefresh(() => apiClient.getNodeInfo(1));
      if (response.success && Array.isArray(response.data)) {
        setPublicNodesCount(response.data.length);
      } else {
        setPublicNodesCount(0);
      }
    } catch (error) {
      setPublicNodesCount(0);
    }
  };

  // Use globalData.userProfile.orgId instead of API call
  React.useEffect(() => {
    if (globalData?.userProfile?.orgId) {
      setOrgId(globalData.userProfile.orgId);
    } else {
      setOrgId('');
    }
  }, [globalData?.userProfile?.orgId]);

  // Load nodes count when orgId is available
  React.useEffect(() => {
    if (orgId) {
      loadPrivateNodesCount();
    }
  }, [orgId]);

  // Fonction pour créer un badge de comptage
  const createCountBadge = (count: number) => (
    <div
      style={{
        width: '24px',
        height: '22px',
        backgroundColor: '#FAFAFA',
        border: '1px solid #E9EAEB',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        fontWeight: 600,
        color: '#344054',
        marginLeft: 'auto',
        marginRight: '8px',
      }}
    >
      {count}
    </div>
  );

  const tabs: TabItem[] = [
    {
      icon: <img src={homeSvg} alt="" style={{ width: '15px', height: '15px' }} />,
      label: 'Home',
      navigateTo: TabIndex.Home,
    },
    {
      icon: <HubIcon size={18} />,
      label: 'Cloud & AI Gateway',
      navigateTo: TabIndex.Hub,
      hidden: true, // Moved to Header
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#98A2B3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />
        </svg>
      ),
      label: 'Cloud Storage',
      navigateTo: -1, // Pas de navigation directe, seulement via le panel hover
      subItems: [
        { label: 'Overview', navigateTo: TabIndex.Dashboard },
        { label: 'Buckets', navigateTo: TabIndex.StorageUnits },
      ],
    },
    {
      icon: <AIIcon size={18} color="#98A2B3" />,
      label: 'AI',
      navigateTo: -1,
      subItems: [
        { label: 'Overview', navigateTo: TabIndex.AIOverview },
        { label: 'LLM', navigateTo: TabIndex.AILLM },
      ],
    },
    {
      icon: <CodesandboxIcon size={18} color="#98A2B3" />,
      label: 'Network',
      navigateTo: TabIndex.BridgeNodes,
      subItems: [
        { label: 'Public Bridge Nodes', navigateTo: TabIndex.BridgeNodesPublic },
        { label: 'Private Bridge Nodes', navigateTo: TabIndex.BridgeNodesPrivate },
      ],
    },
    {
      icon: <FolderIcon sx={{ fontSize: 18, color: '#98A2B3' }} />,
      label: 'Repositories',
      navigateTo: -1, // Pas de navigation directe, seulement via le panel hover
      subItems: [
        { label: 'Overview', navigateTo: TabIndex.RepoOverview },
        { label: 'Inventory', navigateTo: TabIndex.StorageRepos },
        { label: 'API Keys', navigateTo: TabIndex.APIKeys },
      ],
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#98A2B3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
        </svg>
      ),
      label: 'Resource API Keys',
      navigateTo: TabIndex.ResourceAPIKeys,
    },
    {
      icon: <StorageIcon />,
      label: 'My Buckets',
      navigateTo: TabIndex.StellarBuckets,
      hidden: true, // Hidden as per user request
    },
    {
      icon: <StorefrontIcon />,
      label: 'Marketplace',
      navigateTo: TabIndex.StellarMarketplace,
      hidden: true, // Hidden as per user request
    },
    {
      icon: <ArchiveIcon />,
      label: 'My Deals',
      navigateTo: TabIndex.StellarMyDeals,
      hidden: true, // Hidden as per user request
    },
    // { icon: <ArchiveIcon />, label: 'File Explorer', navigateTo: TabIndex.FileExplorer },
  ];

  // Organization-specific sidebar items (shown when on /organization page)
  const organizationTabs: TabItem[] = [
    {
      icon: <HomeIcon sx={{ fontSize: 18 }} />,
      label: 'Home',
      navigateTo: -1, // Special handling for organization sections
    },
    {
      icon: <GroupsIcon sx={{ fontSize: 18 }} />,
      label: 'Team',
      navigateTo: -1,
    },
    {
      icon: <WorkspacesIcon sx={{ fontSize: 18 }} />,
      label: 'Workspace',
      navigateTo: -1,
    },
    {
      icon: <GavelIcon sx={{ fontSize: 18 }} />,
      label: 'Policies',
      navigateTo: -1,
      subItems: [
        { label: 'LLM Guardrails', navigateTo: -1 },
      ],
    },
    {
      icon: <BarChartIcon sx={{ fontSize: 18 }} />,
      label: 'Statistics',
      navigateTo: -1,
      subItems: [
        { label: 'Gateway', navigateTo: -1 },
        { label: 'Private Chat', navigateTo: -1 },
        { label: 'Policies', navigateTo: -1 },
        { label: 'Credits', navigateTo: -1 },
      ],
    },
    {
      icon: <PaymentIcon sx={{ fontSize: 18 }} />,
      label: 'Billing',
      navigateTo: -1,
    },
    ...(authState?.user?.email?.endsWith('@flashback.tech') ? [{
      icon: <LinkIcon sx={{ fontSize: 18 }} />,
      label: 'Activation Links',
      navigateTo: -1,
    }] : []),
  ];

  // Use organization tabs when on organization page, otherwise use regular tabs
  const activeTabs = isOnOrganizationPage ? organizationTabs : tabs;

  // Activation Links has been moved to Organization page

  const toggleExpanded = (index: number) => {
    setExpandedTabs((prev) => {
      // If already open, close it; otherwise open this and close all others (exclusive behavior)
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index);
      }
      return [index];
    });
  };

  const handleProfileClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setAnchorEl(null);
  };

  // Check if user has workspaces and trigger tutorial if not
  const checkAndTriggerTutorial = (tutorialType: 'gateway' | 'depin') => {
    const hasWorkspaces = (globalData?.workspaces?.length || 0) > 0;
    const tutorialKey = tutorialType === 'gateway' ? 'gatewayTutorialCompleted' : 'depinTutorialCompleted';
    
    try {
      const tutorialCompleted = localStorage.getItem(tutorialKey);
      if (!hasWorkspaces && !tutorialCompleted) {
        // Dispatch event to App.tsx to show the tutorial
        window.dispatchEvent(new CustomEvent('triggerMicroTutorial', { 
          detail: { type: tutorialType } 
        }));
        return true; // Tutorial triggered, block navigation
      }
    } catch { /* ignore localStorage errors */ }
    return false; // No tutorial triggered, proceed with navigation
  };

  const handleClick = (index: number, navigateTo: number) => {
    // Prevent any action for coming soon items
    if (index >= 0 && activeTabs[index]?.comingSoon) {
      return;
    }
    
    // Handle Organization section navigation
    if (isOnOrganizationPage && index >= 0) {
      const label = organizationTabs[index]?.label;
      
      // Organization sections use URL params
      const tabMapping: Record<string, string> = {
        'Home': '',
        'Team': 'team',
        'Workspace': 'workspace',
        'Statistics': 'statistics',
        'Billing': 'billing',
        'Activation Links': 'activation-links',
        'Policies': 'policies',
      };
      
      const tabParam = tabMapping[label || ''];
      if (tabParam !== undefined) {
        // Navigate to organization with the appropriate tab param
        if (tabParam) {
          navigate(`/organization?tab=${tabParam}`);
        } else {
          navigate('/organization');
        }
        // Dispatch event to notify Organization component
        window.dispatchEvent(new CustomEvent('organizationTabChange', { detail: { tab: label } }));
        return;
      }
    }
    
    // Special case: Cloud Storage - click navigates to Overview directly
    if (index >= 0 && tabs[index]?.label === 'Cloud Storage') {
      // Check for Gateway tutorial trigger
      if (checkAndTriggerTutorial('gateway')) {
        return; // Tutorial triggered, block navigation
      }
      
      // Sur mobile, toggle le dropdown au lieu d'ouvrir le panel latéral
      if (isMobile) {
        setExpandedTabs((prev) =>
          prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
        );
        return;
      }

      // Sur desktop, cliquer navigue directement vers Overview
      handleTabChange({} as React.SyntheticEvent, TabIndex.Dashboard);
      return;
    }

    // Special case: Network opens a right-side card instead of toggling dropdown
    if (index >= 0 && tabs[index]?.label === 'Network') {
      // Sur mobile, toggle le dropdown au lieu d'ouvrir le panel latéral
      if (isMobile) {
        setExpandedTabs((prev) =>
          prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
        );
        return;
      }

      // Sur desktop, le panel s'ouvre au hover uniquement, donc on ne fait rien au clic
      return;
    }

    // Special case: ISP Market - click navigates to AI LLM directly
    if (index >= 0 && tabs[index]?.label === 'ISP Market') {
      // Check for ISP Market tutorial trigger
      if (checkAndTriggerTutorial('depin')) {
        return; // Tutorial triggered, block navigation
      }
      
      // Sur mobile, toggle le dropdown au lieu d'ouvrir le panel latéral
      if (isMobile) {
        setExpandedTabs((prev) =>
          prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
        );
        return;
      }

      // Sur desktop, cliquer navigue directement vers AI LLM
      handleTabChange({} as React.SyntheticEvent, TabIndex.AILLM);
      return;
    }

    // Special case: AI - click navigates to AI Overview
    if (index >= 0 && tabs[index]?.label === 'AI') {
      // Check for Gateway tutorial trigger (AI is part of Gateway)
      if (checkAndTriggerTutorial('gateway')) {
        return; // Tutorial triggered, block navigation
      }
      
      // Sur mobile, toggle le dropdown au lieu d'ouvrir le panel latéral
      if (isMobile) {
        setExpandedTabs((prev) =>
          prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
        );
        return;
      }

      // Sur desktop, cliquer navigue directement vers AI Overview
      handleTabChange({} as React.SyntheticEvent, TabIndex.AIOverview);
      return;
    }

    // Special case: Repositories - click navigates to Repo Overview directly
    if (index >= 0 && tabs[index]?.label === 'Repositories') {
      // Sur mobile, toggle le dropdown au lieu d'ouvrir le panel latéral
      if (isMobile) {
        setExpandedTabs((prev) =>
          prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
        );
        return;
      }

      // Sur desktop, cliquer navigue directement vers Repo Overview
      handleTabChange({} as React.SyntheticEvent, TabIndex.RepoOverview);
      return;
    }

    // Naviguer vers la page si navigateTo est défini
    // Pour Overview (TabIndex.Dashboard = 0), on doit vérifier explicitement
    if (navigateTo === TabIndex.Dashboard || (navigateTo !== undefined && navigateTo !== 0)) {
      handleTabChange({} as React.SyntheticEvent, navigateTo);
      // Close mobile menu after navigation - SAUF si on est sur mobile et dans un sous-menu
      // (on veut garder le menu ouvert pour permettre d'autres clics)
      if (!isMobile) {
        setIsMobileMenuOpen(false);
      }
    }

    // Gérer l'expansion des sous-menus uniquement si l'élément a des sous-éléments
    if (index >= 0 && tabs[index]?.subItems) {
      toggleExpanded(index);
    }
  };

  const handleMobileItemClick = (index: number, navigateTo: number) => {
    // Ne pas fermer le menu si on clique sur Cloud Storage, Network, ISP Market, Repositories, AI ou Notifications sur mobile
    // (ils ouvrent juste un dropdown)
    const tab = tabs[index];
    const isDropdownItem =
      isMobile &&
      (tab?.label === 'Cloud Storage' ||
        tab?.label === 'Network' ||
        tab?.label === 'ISP Market' ||
        tab?.label === 'Repositories' ||
        tab?.label === 'AI' ||
        tab?.label === 'Notifications');

    handleClick(index, navigateTo);

    // Fermer le menu seulement si ce n'est pas un dropdown sur mobile
    if (!isDropdownItem) {
      setIsMobileMenuOpen(false);
    }
  };

  // Onboarding checklist configuration (for the sidebar Complete account card)
  const onboardingItems = [
    'Complete your profile',
    'Set up 2FA and backup',
    'Create your first bucket',
    'Create your first repository',
    'Generate a Repo API key',
    'Upload your first file',
  ] as const;

  const completionMap: Record<(typeof onboardingItems)[number], boolean> = {
    'Complete your profile': true, // default: validated
    'Set up 2FA and backup': !!mfaState.status?.isEnabled,
    'Create your first bucket': bucketsCount > 0,
    'Create your first repository': repositoriesCount > 0,
    'Generate a Repo API key': hasRepoWithApiKey,
    'Upload your first file': hasUploadedData,
  };

  const completedCount = onboardingItems.filter((label) => completionMap[label]).length;
  const progressPercent = Math.round((completedCount / onboardingItems.length) * 100);

  // Check if user has hidden the onboarding tutorial
  const [hideTutorial, setHideTutorial] = React.useState(() => {
    try {
      return localStorage.getItem('hideOnboardingTutorial') === 'true';
    } catch {
      return false;
    }
  });
  // Check if account settings completed (6/6) - with localStorage persistence
  const [isAccountSettingsCompleted, setIsAccountSettingsCompleted] = React.useState(() => {
    try {
      return localStorage.getItem('accountSettingsCompleted') === 'true';
    } catch {
      return false;
    }
  });
  // Confirmation dialog for skipping tutorial
  const [showSkipConfirmDialog, setShowSkipConfirmDialog] = React.useState(false);
  // Right-side account settings tab visibility
  const [showAccountSettingsTab, setShowAccountSettingsTab] = React.useState(false);
  const accountCardRef = React.useRef<HTMLDivElement | null>(null);
  const [accountPanelPos, setAccountPanelPos] = React.useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const updateAccountPanelPosition = React.useCallback(() => {
    const el = accountCardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // For fixed positioning, use viewport coordinates directly
    setAccountPanelPos({
      top: Math.max(0, Math.round(rect.top - 40)),
      left: Math.round(rect.right + 22),
    });
  }, []);

  React.useEffect(() => {
    if (!showAccountSettingsTab) return;
    updateAccountPanelPosition();
    const onResize = () => updateAccountPanelPosition();
    const onScroll = () => updateAccountPanelPosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [showAccountSettingsTab, updateAccountPanelPosition]);

  // Auto-hide Account Settings when all items completed (6/6) and save to localStorage
  React.useEffect(() => {
    if (completedCount === onboardingItems.length && !isAccountSettingsCompleted) {
      setIsAccountSettingsCompleted(true);
      try {
        localStorage.setItem('accountSettingsCompleted', 'true');
      } catch (error) { /* Ignore errors */ }
    }
  }, [completedCount, onboardingItems.length, isAccountSettingsCompleted]);

  // Use centralized org access from AuthContext
  const [showOrganizationModal, setShowOrganizationModal] = React.useState<boolean>(false);
  const [orgModalInitialSection, setOrgModalInitialSection] = React.useState<
    'Details' | 'Plan' | 'Billing' | 'Activity'
  >('Details');
  const [orgModalInitialShowHover, setOrgModalInitialShowHover] = React.useState<boolean>(false);
  // Organization access is now handled by AuthContext

  // Cloud Storage panel visibility and positioning
  const [showCloudStorageTab, setShowCloudStorageTab] = React.useState(false);
  const cloudCardRef = React.useRef<HTMLButtonElement | null>(null);
  const [cloudPanelPos, setCloudPanelPos] = React.useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const updateCloudPanelPosition = React.useCallback(() => {
    const el = cloudCardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // Place the panel to the right of the menu item, similar to Account Settings panel
    setCloudPanelPos({
      top: Math.max(0, Math.round(rect.top - 40)),
      left: Math.round(rect.right + 22),
    });
  }, []);

  React.useEffect(() => {
    if (!showCloudStorageTab) return;
    updateCloudPanelPosition();
    const onResize = () => updateCloudPanelPosition();
    const onScroll = () => updateCloudPanelPosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [showCloudStorageTab, updateCloudPanelPosition]);

  // Network panel visibility and positioning
  const [showNetworkTab, setShowNetworkTab] = React.useState(false);
  const networkCardRef = React.useRef<HTMLButtonElement | null>(null);
  const [networkPanelPos, setNetworkPanelPos] = React.useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  // Repositories panel visibility and positioning
  const [showRepositoriesTab, setShowRepositoriesTab] = React.useState(false);
  const repositoriesCardRef = React.useRef<HTMLButtonElement | null>(null);
  const [repositoriesPanelPos, setRepositoriesPanelPos] = React.useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const updateNetworkPanelPosition = React.useCallback(() => {
    const el = networkCardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setNetworkPanelPos({
      top: Math.max(0, Math.round(rect.top - 40)),
      left: Math.round(rect.right + 22),
    });
  }, []);

  React.useEffect(() => {
    if (!showNetworkTab) return;
    updateNetworkPanelPosition();
    const onResize = () => updateNetworkPanelPosition();
    const onScroll = () => updateNetworkPanelPosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [showNetworkTab, updateNetworkPanelPosition]);

  const updateRepositoriesPanelPosition = React.useCallback(() => {
    const el = repositoriesCardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setRepositoriesPanelPos({
      top: Math.max(0, Math.round(rect.top - 40)),
      left: Math.round(rect.right + 22),
    });
  }, []);

  React.useEffect(() => {
    if (!showRepositoriesTab) return;
    updateRepositoriesPanelPosition();
    const onResize = () => updateRepositoriesPanelPosition();
    const onScroll = () => updateRepositoriesPanelPosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [showRepositoriesTab, updateRepositoriesPanelPosition]);

  // ISP Market panel visibility and positioning
  const [showDepinHubTab, setShowDepinHubTab] = React.useState(false);
  const depinHubCardRef = React.useRef<HTMLButtonElement | null>(null);
  const [depinHubPanelPos, setDepinHubPanelPos] = React.useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const updateDepinHubPanelPosition = React.useCallback(() => {
    const el = depinHubCardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setDepinHubPanelPos({
      top: Math.round(rect.top),
      left: Math.round(rect.right + 12),
    });
  }, []);

  React.useEffect(() => {
    if (!showDepinHubTab) return;
    updateDepinHubPanelPosition();
    const onResize = () => updateDepinHubPanelPosition();
    const onScroll = () => updateDepinHubPanelPosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [showDepinHubTab, updateDepinHubPanelPosition]);

  // AI panel visibility and positioning
  const [showAITab, setShowAITab] = React.useState(false);
  const aiCardRef = React.useRef<HTMLButtonElement | null>(null);
  const [aiPanelPos, setAIPanelPos] = React.useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const updateAIPanelPosition = React.useCallback(() => {
    const el = aiCardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setAIPanelPos({
      top: Math.round(rect.top),
      left: Math.round(rect.right + 12),
    });
  }, []);

  React.useEffect(() => {
    if (!showAITab) return;
    updateAIPanelPosition();
    const onResize = () => updateAIPanelPosition();
    const onScroll = () => updateAIPanelPosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [showAITab, updateAIPanelPosition]);

  // Notifications hover card (System Events)
  const [showNotificationsTab, setShowNotificationsTab] = React.useState(false);
  const notificationsCardRef = React.useRef<HTMLButtonElement | null>(null);
  const [notificationsPanelPos, setNotificationsPanelPos] = React.useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });
  const [eventsLoading, setEventsLoading] = React.useState(false);
  const [systemEvents, setSystemEvents] = React.useState<
    SystemEventTypes.SystemEventResponse[]
  >([]);
  const [usingOrgFallback, setUsingOrgFallback] = React.useState(false);
  const [userEmailMap, setUserEmailMap] = React.useState<Record<string, string>>({});
  const notifHideTimeoutRef = React.useRef<number | null>(null);
  const accountHideTimeoutRef = React.useRef<number | null>(null);
  const cloudHideTimeoutRef = React.useRef<number | null>(null);
  const networkHideTimeoutRef = React.useRef<number | null>(null);
  const depinHubHideTimeoutRef = React.useRef<number | null>(null);
  const repositoriesHideTimeoutRef = React.useRef<number | null>(null);
  const aiHideTimeoutRef = React.useRef<number | null>(null);
  const teamHideTimeoutRef = React.useRef<number | null>(null);
  const workspaceHideTimeoutRef = React.useRef<number | null>(null);

  // Team panel visibility and positioning (for Organization page)
  const [showTeamTab, setShowTeamTab] = React.useState(false);
  const teamCardRef = React.useRef<HTMLButtonElement | null>(null);
  const [teamPanelPos, setTeamPanelPos] = React.useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const updateTeamPanelPosition = React.useCallback(() => {
    const el = teamCardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setTeamPanelPos({
      top: Math.max(0, Math.round(rect.top - 40)),
      left: Math.round(rect.right + 22),
    });
  }, []);

  React.useEffect(() => {
    if (!showTeamTab) return;
    updateTeamPanelPosition();
    const onResize = () => updateTeamPanelPosition();
    const onScroll = () => updateTeamPanelPosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [showTeamTab, updateTeamPanelPosition]);

  // Workspace panel visibility and positioning (for Organization page)
  const [showWorkspaceTab, setShowWorkspaceTab] = React.useState(false);
  const workspaceCardRef = React.useRef<HTMLButtonElement | null>(null);
  const [workspacePanelPos, setWorkspacePanelPos] = React.useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const updateWorkspacePanelPosition = React.useCallback(() => {
    const el = workspaceCardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setWorkspacePanelPos({
      top: Math.max(0, Math.round(rect.top - 40)),
      left: Math.round(rect.right + 22),
    });
  }, []);

  React.useEffect(() => {
    if (!showWorkspaceTab) return;
    updateWorkspacePanelPosition();
    const onResize = () => updateWorkspacePanelPosition();
    const onScroll = () => updateWorkspacePanelPosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [showWorkspaceTab, updateWorkspacePanelPosition]);

  // Policies panel visibility and positioning (for Organization page)
  const [showPoliciesTab, setShowPoliciesTab] = React.useState(false);
  const policiesCardRef = React.useRef<HTMLButtonElement | null>(null);
  const [policiesPanelPos, setPoliciesPanelPos] = React.useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const policiesHideTimeoutRef = React.useRef<number | null>(null);

  const updatePoliciesPanelPosition = React.useCallback(() => {
    const el = policiesCardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPoliciesPanelPos({
      top: Math.max(0, Math.round(rect.top - 40)),
      left: Math.round(rect.right + 22),
    });
  }, []);

  React.useEffect(() => {
    if (!showPoliciesTab) return;
    updatePoliciesPanelPosition();
    const onResize = () => updatePoliciesPanelPosition();
    const onScroll = () => updatePoliciesPanelPosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [showPoliciesTab, updatePoliciesPanelPosition]);

  // Statistics panel visibility and positioning (for Organization page)
  const [showStatisticsTab, setShowStatisticsTab] = React.useState(false);
  const statisticsCardRef = React.useRef<HTMLButtonElement | null>(null);
  const [statisticsPanelPos, setStatisticsPanelPos] = React.useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const statisticsHideTimeoutRef = React.useRef<number | null>(null);

  const updateStatisticsPanelPosition = React.useCallback(() => {
    const el = statisticsCardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setStatisticsPanelPos({
      top: Math.max(0, Math.round(rect.top - 40)),
      left: Math.round(rect.right + 22),
    });
  }, []);

  React.useEffect(() => {
    if (!showStatisticsTab) return;
    updateStatisticsPanelPosition();
    const onResize = () => updateStatisticsPanelPosition();
    const onScroll = () => updateStatisticsPanelPosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [showStatisticsTab, updateStatisticsPanelPosition]);

  const cancelStatisticsHide = () => {
    if (statisticsHideTimeoutRef.current) {
      window.clearTimeout(statisticsHideTimeoutRef.current);
      statisticsHideTimeoutRef.current = null;
    }
  };
  const scheduleStatisticsHide = () => {
    cancelStatisticsHide();
    statisticsHideTimeoutRef.current = window.setTimeout(() => {
      setShowStatisticsTab(false);
    }, 220);
  };

  const cancelPoliciesHide = () => {
    if (policiesHideTimeoutRef.current) {
      window.clearTimeout(policiesHideTimeoutRef.current);
      policiesHideTimeoutRef.current = null;
    }
  };
  const schedulePoliciesHide = () => {
    cancelPoliciesHide();
    policiesHideTimeoutRef.current = window.setTimeout(() => {
      setShowPoliciesTab(false);
    }, 220);
  };

  const cancelNotifHide = () => {
    if (notifHideTimeoutRef.current) {
      window.clearTimeout(notifHideTimeoutRef.current);
      notifHideTimeoutRef.current = null;
    }
  };
  const scheduleNotifHide = () => {
    cancelNotifHide();
    notifHideTimeoutRef.current = window.setTimeout(() => {
      setShowNotificationsTab(false);
    }, 220);
  };

  const cancelAccountHide = () => {
    if (accountHideTimeoutRef.current) {
      window.clearTimeout(accountHideTimeoutRef.current);
      accountHideTimeoutRef.current = null;
    }
  };
  const scheduleAccountHide = () => {
    cancelAccountHide();
    accountHideTimeoutRef.current = window.setTimeout(() => {
      setShowAccountSettingsTab(false);
    }, 220);
  };

  const cancelCloudHide = () => {
    if (cloudHideTimeoutRef.current) {
      window.clearTimeout(cloudHideTimeoutRef.current);
      cloudHideTimeoutRef.current = null;
    }
  };
  const scheduleCloudHide = () => {
    cancelCloudHide();
    cloudHideTimeoutRef.current = window.setTimeout(() => {
      setShowCloudStorageTab(false);
    }, 220);
  };

  const cancelNetworkHide = () => {
    if (networkHideTimeoutRef.current) {
      window.clearTimeout(networkHideTimeoutRef.current);
      networkHideTimeoutRef.current = null;
    }
  };
  const scheduleNetworkHide = () => {
    cancelNetworkHide();
    networkHideTimeoutRef.current = window.setTimeout(() => {
      setShowNetworkTab(false);
    }, 220);
  };

  const cancelDepinHubHide = () => {
    if (depinHubHideTimeoutRef.current) {
      window.clearTimeout(depinHubHideTimeoutRef.current);
      depinHubHideTimeoutRef.current = null;
    }
  };
  const scheduleDepinHubHide = () => {
    cancelDepinHubHide();
    depinHubHideTimeoutRef.current = window.setTimeout(() => {
      setShowDepinHubTab(false);
    }, 220);
  };

  const cancelRepositoriesHide = () => {
    if (repositoriesHideTimeoutRef.current) {
      window.clearTimeout(repositoriesHideTimeoutRef.current);
      repositoriesHideTimeoutRef.current = null;
    }
  };
  const scheduleRepositoriesHide = () => {
    cancelRepositoriesHide();
    repositoriesHideTimeoutRef.current = window.setTimeout(() => {
      setShowRepositoriesTab(false);
    }, 220);
  };

  const cancelAIHide = () => {
    if (aiHideTimeoutRef.current) {
      window.clearTimeout(aiHideTimeoutRef.current);
      aiHideTimeoutRef.current = null;
    }
  };

  const cancelTeamHide = () => {
    if (teamHideTimeoutRef.current) {
      window.clearTimeout(teamHideTimeoutRef.current);
      teamHideTimeoutRef.current = null;
    }
  };
  const scheduleTeamHide = () => {
    cancelTeamHide();
    teamHideTimeoutRef.current = window.setTimeout(() => {
      setShowTeamTab(false);
    }, 220);
  };
  const cancelWorkspaceHide = () => {
    if (workspaceHideTimeoutRef.current) {
      window.clearTimeout(workspaceHideTimeoutRef.current);
      workspaceHideTimeoutRef.current = null;
    }
  };
  const scheduleWorkspaceHide = () => {
    cancelWorkspaceHide();
    workspaceHideTimeoutRef.current = window.setTimeout(() => {
      setShowWorkspaceTab(false);
    }, 220);
  };
  const scheduleAIHide = () => {
    cancelAIHide();
    aiHideTimeoutRef.current = window.setTimeout(() => {
      setShowAITab(false);
    }, 220);
  };

  const updateNotificationsPanelPosition = React.useCallback(() => {
    const el = notificationsCardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // Raise the panel even higher above the trigger
    setNotificationsPanelPos({
      top: Math.max(0, Math.round(rect.top - 380)),
      left: Math.round(rect.right + 12),
    });
  }, []);

  // Helper: get notification icon based on event type
  const getNotificationIcon = (event: string): string => {
    const evt = event.toLowerCase();
    if (evt === 'created') return checkNotifsIcon;
    if (evt === 'updated' || evt === 'changed') return reloadNotifsIcon;
    if (evt === 'deleted' || evt === 'invalid') return warningNotifsIcon;
    // Default to check icon for other events
    return checkNotifsIcon;
  };

  const loadSystemEvents = React.useCallback(async () => {
    try {
      setEventsLoading(true);
      setUsingOrgFallback(false);
      const data: SystemEventTypes.SystemEventQueryRequest = {
        skip: 0,
        take: 50,
      };
      const res = await withTokenRefresh(() => apiClient.getSystemEvents(data));
      if (res && Array.isArray(res.events)) setSystemEvents(res.events);
      else setSystemEvents([]);
    } catch (e) {
      setSystemEvents([]);
    } finally {
      setEventsLoading(false);
    }
  }, [withTokenRefresh, apiClient]);

  React.useEffect(() => {
    if (!showNotificationsTab) return;
    updateNotificationsPanelPosition();
    loadSystemEvents();
    const onResize = () => updateNotificationsPanelPosition();
    const onScroll = () => updateNotificationsPanelPosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [showNotificationsTab, updateNotificationsPanelPosition, loadSystemEvents]);

  // Use centralized workspace data from AuthContext
  // Create workspace dialog state
  const [createOpen, setCreateOpen] = React.useState(false);
  const [wsName, setWsName] = React.useState('');
  const [creating, setCreating] = React.useState(false);
  const [createError, setCreateError] = React.useState<string | null>(null);

  // Listen for bucket updates to refresh counts
  React.useEffect(() => {
    const onWorkspaceChanged = () => {
      // Force refresh to bypass cache when workspace changes
      // Use WorkspaceStatsService instead of separate loadBucketsCount/loadRepositoriesCount
      loadWorkspaceStats(true);
      refreshQuota(); // Refresh quota from QuotaContext
      loadPrivateNodesCount();
      loadPublicNodesCount();
    };
    const onBucketsUpdated = () => {
      // Actualiser les compteurs quand la liste de buckets change
      // Use WorkspaceStatsService instead of separate loadBucketsCount
      loadWorkspaceStats(true);
      refreshQuota(); // Refresh quota from QuotaContext
    };
    const onRepoApiKeyCreated = () => {
      // Actualiser les repos pour détecter la nouvelle API key et mettre à jour la quête
      // Use WorkspaceStatsService instead of separate loadRepositoriesCount
      loadWorkspaceStats(true);
    };
    const onOpenOrganizationModal = (event: any) => {
      const { section, showHover } = event.detail || {};
      setOrgModalInitialSection(section || 'Details');
      setOrgModalInitialShowHover(showHover || false);
      setShowOrganizationModal(true);
    };
    const onOpenProfileModal = (event: any) => {
      const { section, tab, highlight2FA } = event.detail || {};
      setProfileModalInitialSection(section || 'Profile');
      if (tab) setProfileModalInitialTab(tab);
      if (highlight2FA) setProfileModalHighlight2FA(true);
      setProfileModalOpen(true);
    };
    window.addEventListener('workspaceChanged', onWorkspaceChanged as any);
    window.addEventListener('bucketsUpdated', onBucketsUpdated as any);
    window.addEventListener('repoApiKeyCreated', onRepoApiKeyCreated as any);
    window.addEventListener('openOrganizationModal', onOpenOrganizationModal as any);
    window.addEventListener('openProfileModal', onOpenProfileModal as any);
    return () => {
      window.removeEventListener('workspaceChanged', onWorkspaceChanged as any);
      window.removeEventListener('bucketsUpdated', onBucketsUpdated as any);
      window.removeEventListener('repoApiKeyCreated', onRepoApiKeyCreated as any);
      window.removeEventListener('openOrganizationModal', onOpenOrganizationModal as any);
      window.removeEventListener('openProfileModal', onOpenProfileModal as any);
    };
  }, []);

  // Listen for trigger to open ProfileModal on Security tab (from OverviewMockup)
  React.useEffect(() => {
    const handleOpenProfileSecurity = () => {
      try {
        const flag = localStorage.getItem('triggerProfileSecurity');
        if (flag === '1') {
          localStorage.removeItem('triggerProfileSecurity');
          setProfileModalInitialSection('Profile');
          setProfileModalInitialTab('Security');
          setProfileModalOpen(true);
        }
      } catch (error) { /* Ignore errors */ }
    };

    // Check on mount
    handleOpenProfileSecurity();

    // Listen for custom event
    window.addEventListener('openProfileSecurity', handleOpenProfileSecurity);
    return () => {
      window.removeEventListener('openProfileSecurity', handleOpenProfileSecurity);
    };
  }, []);

  const handleCreateWorkspace = async () => {
    if (!wsName.trim()) {
      setCreateError('Workspace name is required');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const res = await withTokenRefresh(() =>
        apiClient.createWorkspace({ name: wsName.trim() })
      );
      if (!res.success || !res.workspace?.id) {
        throw new Error('Failed to create workspace');
      }
      
      // Notify AuthContext to reload workspaces
      try {
        window.dispatchEvent(new CustomEvent('forceReloadWorkspaces'));
      } catch (e) {
        console.warn('[Sidebar] Failed to dispatch forceReloadWorkspaces event:', e);
      }
      
      // Select the new workspace
      setSelectedWorkspace(res.workspace.id);
      setCreateOpen(false);
      setWsName('');
    } catch (e: any) {
      setCreateError(e?.message || 'Failed to create workspace');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      className={`
        fixed
        ${isFullWidthHeaderPage ? 'top-[64px]' : 'top-0'} bottom-0 left-0
        w-[296px]
        transform transition-transform duration-300 ease-in-out
        ${sidebarCollapsed ? '' : 'lg:transform-none'}
        z-30
        ${isMobileMenuOpen ? 'translate-x-0' : sidebarCollapsed ? '-translate-x-full' : '-translate-x-full lg:translate-x-0'}
        border-r border-gray-200
        flex flex-col
        bg-white shadow-lg
        overflow-visible
      `}
      style={{
        backgroundColor: '#FFFFFF',
        height: isFullWidthHeaderPage ? 'calc(100vh - 64px)' : '100vh',
        minHeight: isFullWidthHeaderPage ? 'calc(100vh - 64px)' : '100vh',
        maxHeight: isFullWidthHeaderPage ? 'calc(100vh - 64px)' : '100vh',
      }}
    >
      {/* Close button for mobile */}
      <button
        className="lg:hidden absolute right-4 top-4 text-gray-600 hover:text-gray-800"
        onClick={() => setIsMobileMenuOpen(false)}
      >
        ✕
      </button>

      {/* Logo and Toggle button - hidden on full-width header pages (logo is in header) */}
      {!isFullWidthHeaderPage && (
        <div className="relative flex items-center justify-center py-2 mt-2">
          <img src="/logoblack.png" alt="Flashback" className="h-8" />
        </div>
      )}



      {/* Workspace selector - HIDDEN (moved to header) */}
      {false && (<div className="flex justify-center mb-2">
        <div className="w-[263px]">
          <label className="block text-[12px] text-gray-500 mb-1">Workspace</label>
          <select
            value={globalData.selectedWorkspaceId}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '__CREATE__') {
                setCreateOpen(true);
                return;
              }
              // Use centralized setSelectedWorkspace from AuthContext
              // It handles localStorage persistence, cache update, and event dispatching
              setSelectedWorkspace(val);
            }}
            className="w-full h-8 border border-gray-200 rounded-md px-2 py-0 text-[13px] leading-[16px] bg-white text-gray-800 overflow-hidden text-ellipsis whitespace-nowrap"
            title={
              globalData.workspaces.find((w: any) => w.id === globalData.selectedWorkspaceId)
                ?.name || 'Select Workspace'
            }
          >
            <option value="__CREATE__">+ Create workspace…</option>
            {globalData.workspaces.length === 0 && (
              <option value="" disabled>
                No accessible workspace
              </option>
            )}
            {globalData.workspaces.map((w: any) => (
              <option key={w.id} value={w.id} title={w.name}>
                {w.name}
              </option>
            ))}
          </select>
        </div>
      </div>)}

      {/* Main navigation menu - starts after logo area (~56px) */}
      <div className="flex-1 space-y-0.5 overflow-y-auto" style={{ marginTop: '8px' }}>
        {/* Section Header - exact match with Chat.tsx sidebar header (p: 2 = 16px, borderBottom) */}
        <div style={{ 
          padding: '16px', 
          borderBottom: '1px solid #E9EAEB',
          display: 'flex',
          alignItems: 'center'
        }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: 600, 
            color: '#101828',
            margin: 0
          }}>
            {isOnChatPage ? 'Private Chat' : isOnDePINHubPage ? 'ISP Market' : isOnOrganizationPage ? 'Organization' : 'Cloud & AI Gateway'}
          </h3>
        </div>
        {/* Workspace selector moved to OverviewMockup */}
        
        {/* ISP Market specific navigation */}
        {isOnDePINHubPage ? (
          <>
            {[
              { label: 'Storage Units', path: '/depin-hub/storage-units', icon: <StorageIcon style={{ fontSize: 18 }} /> },
              { label: 'Marketplace', path: '/depin-hub/marketplace', icon: <StorefrontIcon style={{ fontSize: 18 }} /> },
              { label: 'Deals', path: '/depin-hub/deals', icon: <HandshakeIcon style={{ fontSize: 18 }} /> },
            ].map((item) => {
              const isActive = location.pathname.includes(item.path.split('/').pop() || '');
              return (
                <div key={item.label}>
                  <button
                    onClick={() => {
                      navigate(item.path);
                    }}
                    className={`
                      w-[263px] h-[40px]
                      flex items-center gap-2
                      transition-colors duration-200
                      relative
                      mx-auto
                      rounded-md
                      ${isActive
                        ? ''
                        : 'hover:bg-gray-50'
                      }
                    `}
                    style={isActive ? { backgroundColor: '#F0F2FF' } : {}}
                  >
                    {isActive && (
                      <div style={{ position: 'absolute', left: 0, top: 8, bottom: 8, width: 3, borderRadius: 2, backgroundColor: '#617BFF' }} />
                    )}
                    <div
                      className="text-sm pl-2 flex items-center justify-center"
                      style={{ minWidth: '24px', minHeight: '24px', color: isActive ? '#617BFF' : '#98A2B3' }}
                    >
                      {item.icon}
                    </div>
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: '14px',
                        lineHeight: '22px',
                        letterSpacing: '0%',
                        color: isActive ? '#617BFF' : '#414651',
                      }}
                    >
                      {item.label}
                    </span>
                  </button>
                </div>
              );
            })}
          </>
        ) : activeTabs.map((tab, index) => {
          if (tab.hidden) return null;
          return (
          <div key={index}>
            <button
              ref={
                tab.label === 'Cloud Storage'
                  ? cloudCardRef
                  : tab.label === 'Network'
                  ? networkCardRef
                  : tab.label === 'ISP Market'
                  ? depinHubCardRef
                  : tab.label === 'Repositories'
                  ? repositoriesCardRef
                  : tab.label === 'AI'
                  ? aiCardRef
                  : (isOnOrganizationPage && tab.label === 'Team')
                  ? teamCardRef
                  : (isOnOrganizationPage && tab.label === 'Workspace')
                  ? workspaceCardRef
                  : (isOnOrganizationPage && tab.label === 'Policies')
                  ? policiesCardRef
                  : (isOnOrganizationPage && tab.label === 'Statistics')
                  ? statisticsCardRef
                  : null
              }
              data-tutorial={
                tab.label === 'AI' ? 'menu-ai' 
                : tab.label === 'Repositories' ? 'menu-repositories'
                : undefined
              }
              onClick={() => {
                if (tab.comingSoon) return; // Prevent navigation for coming soon items
                handleMobileItemClick(index, tab.navigateTo ?? 0);
              }}
              onMouseEnter={() => {
                if (tab.comingSoon) return; // Prevent hover panels for coming soon items
                if (!isMobile && tab.label === 'Cloud Storage') {
                  cancelCloudHide();
                  setShowCloudStorageTab(true);
                  setShowNetworkTab(false);
                  setShowDepinHubTab(false);
                  setShowRepositoriesTab(false);
                  setShowAITab(false);
                  setShowAccountSettingsTab(false);
                  setShowNotificationsTab(false);
                  updateCloudPanelPosition();
                } else if (!isMobile && tab.label === 'Network') {
                  cancelNetworkHide();
                  setShowNetworkTab(true);
                  setShowCloudStorageTab(false);
                  setShowDepinHubTab(false);
                  setShowRepositoriesTab(false);
                  setShowAITab(false);
                  setShowAccountSettingsTab(false);
                  setShowNotificationsTab(false);
                  updateNetworkPanelPosition();
                } else if (!isMobile && tab.label === 'Repositories') {
                  cancelRepositoriesHide();
                  setShowRepositoriesTab(true);
                  setShowCloudStorageTab(false);
                  setShowNetworkTab(false);
                  setShowDepinHubTab(false);
                  setShowAITab(false);
                  setShowAccountSettingsTab(false);
                  setShowNotificationsTab(false);
                  updateRepositoriesPanelPosition();
                } else if (!isMobile && tab.label === 'ISP Market') {
                  cancelDepinHubHide();
                  setShowDepinHubTab(true);
                  setShowCloudStorageTab(false);
                  setShowNetworkTab(false);
                  setShowRepositoriesTab(false);
                  setShowAITab(false);
                  setShowAccountSettingsTab(false);
                  setShowNotificationsTab(false);
                  updateDepinHubPanelPosition();
                } else if (!isMobile && tab.label === 'AI') {
                  cancelAIHide();
                  setShowAITab(true);
                  setShowCloudStorageTab(false);
                  setShowNetworkTab(false);
                  setShowDepinHubTab(false);
                  setShowRepositoriesTab(false);
                  setShowAccountSettingsTab(false);
                  setShowNotificationsTab(false);
                  setShowTeamTab(false);
                  updateAIPanelPosition();
                } else if (!isMobile && isOnOrganizationPage && tab.label === 'Team') {
                  cancelTeamHide();
                  setShowTeamTab(true);
                  setShowWorkspaceTab(false);
                  setShowPoliciesTab(false);
                  setShowStatisticsTab(false);
                  setShowCloudStorageTab(false);
                  setShowNetworkTab(false);
                  setShowDepinHubTab(false);
                  setShowRepositoriesTab(false);
                  setShowAITab(false);
                  setShowAccountSettingsTab(false);
                  setShowNotificationsTab(false);
                  updateTeamPanelPosition();
                } else if (!isMobile && isOnOrganizationPage && tab.label === 'Workspace') {
                  cancelWorkspaceHide();
                  setShowWorkspaceTab(true);
                  setShowTeamTab(false);
                  setShowPoliciesTab(false);
                  setShowStatisticsTab(false);
                  setShowCloudStorageTab(false);
                  setShowNetworkTab(false);
                  setShowDepinHubTab(false);
                  setShowRepositoriesTab(false);
                  setShowAITab(false);
                  setShowAccountSettingsTab(false);
                  setShowNotificationsTab(false);
                  updateWorkspacePanelPosition();
                } else if (!isMobile && isOnOrganizationPage && tab.label === 'Policies') {
                  cancelPoliciesHide();
                  setShowPoliciesTab(true);
                  setShowTeamTab(false);
                  setShowWorkspaceTab(false);
                  setShowStatisticsTab(false);
                  setShowCloudStorageTab(false);
                  setShowNetworkTab(false);
                  setShowDepinHubTab(false);
                  setShowRepositoriesTab(false);
                  setShowAITab(false);
                  setShowAccountSettingsTab(false);
                  setShowNotificationsTab(false);
                  updatePoliciesPanelPosition();
                } else if (!isMobile && isOnOrganizationPage && tab.label === 'Statistics') {
                  cancelStatisticsHide();
                  setShowStatisticsTab(true);
                  setShowTeamTab(false);
                  setShowWorkspaceTab(false);
                  setShowPoliciesTab(false);
                  setShowCloudStorageTab(false);
                  setShowNetworkTab(false);
                  setShowDepinHubTab(false);
                  setShowRepositoriesTab(false);
                  setShowAITab(false);
                  setShowAccountSettingsTab(false);
                  setShowNotificationsTab(false);
                  updateStatisticsPanelPosition();
                }
              }}
              onMouseLeave={() => {
                if (!isMobile && tab.label === 'Cloud Storage') {
                  scheduleCloudHide();
                } else if (!isMobile && tab.label === 'Network') {
                  scheduleNetworkHide();
                } else if (!isMobile && tab.label === 'Repositories') {
                  scheduleRepositoriesHide();
                } else if (!isMobile && tab.label === 'ISP Market') {
                  scheduleDepinHubHide();
                } else if (!isMobile && tab.label === 'AI') {
                  scheduleAIHide();
                } else if (!isMobile && isOnOrganizationPage && tab.label === 'Team') {
                  scheduleTeamHide();
                } else if (!isMobile && isOnOrganizationPage && tab.label === 'Workspace') {
                  scheduleWorkspaceHide();
                } else if (!isMobile && isOnOrganizationPage && tab.label === 'Policies') {
                  schedulePoliciesHide();
                } else if (!isMobile && isOnOrganizationPage && tab.label === 'Statistics') {
                  scheduleStatisticsHide();
                }
              }}
              className={`
                w-[263px] h-[40px]
                flex items-center gap-2
                transition-colors duration-200
                relative
                mx-auto
                ${tab.comingSoon 
                  ? 'opacity-60 cursor-not-allowed text-gray-400'
                  : (isOnOrganizationPage && tab.label === activeOrgSection) ||
                    (!isOnOrganizationPage && (tabValue === tab.navigateTo ||
                    (tab.subItems && tab.subItems.some((item) => item.navigateTo === tabValue))))
                    ? 'bg-[#FAFAFA] text-gray-600 font-semibold rounded-md'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50 hover:border hover:border-gray-200 hover:rounded-md'
                }
              `}
            >
              {/* Suppression de la barre bleue à gauche */}
              <div
                className="text-sm pl-2 flex items-center justify-center"
                style={{ minWidth: '24px', minHeight: '24px' }}
              >
                {tab.icon}
              </div>
              <span
                style={{
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0%',
                  color: tab.comingSoon
                    ? '#9CA3AF'
                    : (isOnOrganizationPage && tab.label === activeOrgSection) ||
                      (!isOnOrganizationPage && (tabValue === tab.navigateTo ||
                      (tab.subItems && tab.subItems.some((item) => item.navigateTo === tabValue))))
                      ? '#252B37'
                      : '#414651',
                }}
              >
                {tab.label}
              </span>
              {tab.comingSoon && (
                <span
                  className="ml-auto mr-3 px-2 py-0.5 text-[10px] font-semibold rounded-full"
                  style={{
                    backgroundColor: '#FEF3C7',
                    color: '#92400E',
                  }}
                >
                  Coming Soon
                </span>
              )}
              {tab.subItems && !tab.comingSoon && (
                <span className="ml-auto mr-3">
                  {expandedTabs.includes(index ?? 0) ? (
                    <svg
                      width="10"
                      height="6"
                      viewBox="0 0 10 6"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 1L5 5L9 1"
                        stroke="currentColor"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <svg
                      width="6"
                      height="10"
                      viewBox="0 0 6 10"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 1L5 5L1 9"
                        stroke="currentColor"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </span>
              )}
              {/* Arrow for Team on organization page (has hover panel) */}
              {isOnOrganizationPage && tab.label === 'Team' && !tab.subItems && (
                <span className="ml-auto mr-3">
                  <svg
                    width="6"
                    height="10"
                    viewBox="0 0 6 10"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 1L5 5L1 9"
                      stroke="currentColor"
                      strokeWidth="1"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              )}
              {/* Arrow for Workspace on organization page (has hover panel) */}
              {isOnOrganizationPage && tab.label === 'Workspace' && !tab.subItems && (
                <span className="ml-auto mr-3">
                  <svg
                    width="6"
                    height="10"
                    viewBox="0 0 6 10"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 1L5 5L1 9"
                      stroke="currentColor"
                      strokeWidth="1"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              )}
              {/* Arrow for Statistics on organization page (has hover panel) */}
              {isOnOrganizationPage && tab.label === 'Statistics' && !tab.subItems && (
                <span className="ml-auto mr-3">
                  <svg
                    width="6"
                    height="10"
                    viewBox="0 0 6 10"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 1L5 5L1 9"
                      stroke="currentColor"
                      strokeWidth="1"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              )}
            </button>

            {/* Submenu items - disabled for Cloud Storage and Network on desktop (handled via right-side cards) */}
            {tab.subItems &&
              !tab.comingSoon &&
              (isMobile || (tab.label !== 'Cloud Storage' && tab.label !== 'Network')) &&
              expandedTabs.includes(index ?? 0) && (
                <div className="flex flex-col items-center mt-0.5 space-y-0.5">
                  {tab.subItems.map((subItem, subIndex) => {
                    return (
                      <button
                        key={subIndex}
                        onClick={() => {
                          if (isOnOrganizationPage) {
                            // Organization sub-item navigation via URL params
                            const parentLabel = tab.label;
                            const tabMapping: Record<string, string> = {
                              'Policies': 'policies',
                              'Statistics': 'statistics',
                              'Team': 'team',
                              'Workspace': 'workspace',
                            };
                            const subMapping: Record<string, string> = {
                              'LLM Guardrails': 'llm-guardrails',
                              'Gateway': 'gateway',
                              'Private Chat': 'chat',
                              'Policies': 'policies',
                              'Members': 'members',
                              'Management': 'management',
                            };
                            const tabParam = tabMapping[parentLabel || ''];
                            const sectionParam = subMapping[subItem.label];
                            if (tabParam && sectionParam) {
                              navigate(`/organization?tab=${tabParam}&section=${sectionParam}`);
                              window.dispatchEvent(new CustomEvent('organizationTabChange', { detail: { tab: parentLabel } }));
                            }
                            return;
                          }
                          handleClick(-1, subItem.navigateTo);
                        }}
                        className={`w-[263px] h-[40px] text-left relative
						text-xs flex items-center gap-2
						mx-auto
						${
              (isOnOrganizationPage
                ? (() => {
                    const subMap: Record<string, string> = { 'LLM Guardrails': 'llm-guardrails', 'Gateway': 'gateway', 'Private Chat': 'chat', 'Policies': 'policies', 'Members': 'members', 'Management': 'management' };
                    return activeOrgSubSection === subMap[subItem.label];
                  })()
                : tabValue === subItem.navigateTo)
                ? 'bg-[#FAFAFA] text-gray-600 font-semibold rounded-md'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100 font-semibold'
            }
						`}
                      >
                        <span
                          className="pl-7"
                          style={{
                            fontWeight: 600,
                            fontSize: '14px',
                            lineHeight: '22px',
                            letterSpacing: '0%',
                            color:
                              (isOnOrganizationPage
                                ? (() => {
                                    const subMap: Record<string, string> = { 'LLM Guardrails': 'llm-guardrails', 'Gateway': 'gateway', 'Private Chat': 'chat', 'Policies': 'policies', 'Members': 'members', 'Management': 'management' };
                                    return activeOrgSubSection === subMap[subItem.label];
                                  })()
                                : tabValue === subItem.navigateTo)
                                ? '#252B37'
                                : '#414651',
                          }}
                        >
                          {subItem.label}
                        </span>
                        {/* Badge de comptage pour Buckets et Inventory */}
                        {subItem.label === 'Buckets' && createCountBadge(bucketsCount)}
                        {subItem.label === 'Inventory' && createCountBadge(repositoriesCount)}
                      </button>
                    );
                  })}
                </div>
              )}
          </div>
          );
        })}

      </div>

      {/* Docs Link - HIDDEN */}
      {false && (<div className="py-1 mt-32 bg-white flex justify-center">
        <button
          onClick={() => {
            try {
              window.open('https://docs.flashback.tech/', '_blank', 'noopener,noreferrer');
            } catch {
              // ignore errors
            }
          }}
          className="w-[263px] h-[40px] rounded-md flex items-center gap-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors mx-auto"
        >
          <div
            className="text-sm pl-2 flex items-center justify-center"
            style={{ minWidth: '24px', minHeight: '24px' }}
          >
            <img src={docsSvg} alt="" style={{ width: '18px', height: '18px' }} />
          </div>
          <span
            style={{
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '22px',
              letterSpacing: '0%',
              color: '#414651',
            }}
          >
            Docs
          </span>
          <span className="ml-auto mr-3 flex items-center">
            <img src={docsLinkSvg} alt="External link" style={{ width: '16px', height: '16px' }} />
          </span>
        </button>
      </div>)}

      {/* Support Link - HIDDEN */}
      {false && (<div className="py-1 bg-white flex justify-center">
        <button
          onClick={() => handleClick(-1, TabIndex.Feedback)}
          className="w-[263px] h-[40px] rounded-md flex items-center gap-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors mx-auto"
        >
          <div
            className="text-sm pl-2 flex items-center justify-center"
            style={{ minWidth: '24px', minHeight: '24px' }}
          >
            <img src={supportSvg} alt="" style={{ width: '18px', height: '18px' }} />
          </div>
          <span
            style={{
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '22px',
              letterSpacing: '0%',
              color: tabValue === TabIndex.Feedback ? '#252B37' : '#414651',
            }}
          >
            Support
          </span>
        </button>
      </div>)}

      {/* Organization Link (between Support and Notifications) - HIDDEN */}
      {false && globalData.hasOrgAccess && globalData.isReady && (
        <div className="py-1 bg-white flex justify-center">
          <button
            onClick={() => {
              setOrgModalInitialSection('Details');
              setOrgModalInitialShowHover(false);
              setShowOrganizationModal(true);
              setIsMobileMenuOpen(false);
            }}
            className="w-[263px] h-[40px] rounded-md flex items-center gap-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors mx-auto"
            title="Organization"
          >
            <div
              className="text-sm pl-2 flex items-center justify-center"
              style={{ minWidth: '24px', minHeight: '24px' }}
            >
              {/* Gray network icon SVG */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#98A2B3"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-network-icon lucide-network"
              >
                <rect x="16" y="16" width="6" height="6" rx="1" />
                <rect x="2" y="16" width="6" height="6" rx="1" />
                <rect x="9" y="2" width="6" height="6" rx="1" />
                <path d="M5 16v-3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3" />
                <path d="M12 12V8" />
              </svg>
            </div>
            <span
              style={{
                fontWeight: 600,
                fontSize: '14px',
                lineHeight: '22px',
                letterSpacing: '0%',
                color: '#414651',
              }}
            >
              Organization
            </span>
          </button>
        </div>
      )}

      {/* Subscription Link - HIDDEN */}
      {false && (<div className="py-1 bg-white flex justify-center">
        <button
          onClick={() => {
            setShowAccountSettingsTab(false);
            setIsMobileMenuOpen(false);

            // Si l'utilisateur a accès à une organisation, ouvrir OrganizationModal
            if (globalData.hasOrgAccess) {
              setOrgModalInitialSection('Plan');
              setOrgModalInitialShowHover(true);
              setShowOrganizationModal(true);
            } else {
              // Sinon, ouvrir ProfileModal sur l'onglet Plan
              setProfileModalInitialSection('Plan');
              setProfileModalOpen(true);
            }
          }}
          className="w-[263px] h-[40px] rounded-md flex items-center gap-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors mx-auto"
        >
          <div
            className="text-sm pl-2 flex items-center justify-center"
            style={{ minWidth: '24px', minHeight: '24px' }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#98A2B3"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
              <line x1="1" y1="10" x2="23" y2="10" />
            </svg>
          </div>
          <span
            style={{
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '22px',
              letterSpacing: '0%',
              color: '#414651',
            }}
          >
            Subscription
          </span>
          <span className="ml-auto mr-3 flex items-center gap-1">
            <span className="text-xs text-gray-600">{getPlanTier(quotaData.subscriptionName)}</span>
            <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: getPlanColor(quotaData.subscriptionName) }} />
          </span>
        </button>
      </div>)}

      {/* Notifications Link - HIDDEN */}
      {false && (<div className="py-1 bg-white flex justify-center">
        <button
          ref={notificationsCardRef}
          onClick={() => {
            // Clic pour naviguer vers Activity
            handleClick(-1, TabIndex.Activity);
            setShowNotificationsTab(false);
            setIsMobileMenuOpen(false);
          }}
          onMouseEnter={() => {
            // Sur desktop seulement - ouvrir le panel au hover
            if (!isMobile) {
              cancelNotifHide();
              setShowNotificationsTab(true);
              setShowAccountSettingsTab(false);
            }
          }}
          onMouseLeave={() => {
            // Sur desktop seulement
            if (!isMobile) {
              scheduleNotifHide();
            }
          }}
          className="w-[263px] h-[40px] rounded-md flex items-center gap-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors mx-auto cursor-pointer"
          aria-label="Notifications"
        >
          <div
            className="text-sm pl-2 flex items-center justify-center"
            style={{ minWidth: '24px', minHeight: '24px' }}
          >
            <img src={bellSvg} alt="" style={{ width: '18px', height: '18px' }} />
          </div>
          <span
            style={{
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '22px',
              letterSpacing: '0%',
              color: '#414651',
            }}
          >
            Notifications
          </span>
        </button>
        {showNotificationsTab && (
          <div
            className="fixed z-50 pointer-events-auto"
            style={{ left: notificationsPanelPos.left, top: notificationsPanelPos.top }}
            onMouseEnter={() => {
              cancelNotifHide();
              setShowNotificationsTab(true);
              setShowAccountSettingsTab(false);
            }}
            onMouseLeave={scheduleNotifHide}
          >
            <div
              className="bg-white border border-[#E9EAEB] p-4 shadow-lg overflow-hidden flex flex-col"
              style={{ width: 540, height: 434, borderRadius: 12 }}
            >
              <div className="flex items-center justify-between mb-3 shrink-0">
                <span style={{ fontWeight: 600, fontSize: '16px', color: '#101828' }}>
                  Notifications
                </span>
                <div className="flex items-center gap-2">
                  {systemEvents.some((ev) => ev.showUnread) && (
                    <button
                      type="button"
                      className="text-[12px] text-[#617BFF] hover:underline font-medium"
                      onClick={() => {
                        const unreadEvents = systemEvents.filter((ev) => ev.showUnread);
                        unreadEvents.forEach((ev) => {
                          withTokenRefresh(() => apiClient.markSystemEventAsRead(ev.id)).catch(() => { /* noop */ });
                        });
                        setSystemEvents((prev) =>
                          prev.map((e) => (e.showUnread ? { ...e, showUnread: false } : e))
                        );
                      }}
                    >
                      Mark all as Read
                    </button>
                  )}
                  <button
                    className="text-gray-500 hover:text-gray-700"
                    onClick={() => setShowNotificationsTab(false)}
                    title="Close"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <div className="flex-1 min-h-0 mt-1">
                {eventsLoading ? (
                  <div className="text-sm text-[#475467] py-2">Loading…</div>
                ) : systemEvents.length === 0 ? (
                  <div className="text-sm text-[#475467] py-2">No recent events</div>
                ) : (
                  <ul className="h-full overflow-auto divide-y divide-[#E9EAEB] pr-1">
                    {systemEvents
                      .filter((ev) => {
                        if (ev.showUnread) return true;
                        const age = Date.now() - new Date(ev.timestamp).getTime();
                        return age < 24 * 60 * 60 * 1000;
                      })
                      .map((ev) => (
                        <li
                          key={ev.id}
                          className={`py-2.5 px-1 transition-colors ${
                            ev.showUnread ? 'bg-[rgba(97,123,255,0.06)] border-l-2 border-[#617BFF]' : 'hover:bg-[#FAFAFA]'
                          }`}
                          onMouseEnter={() => {
                            if (ev.showUnread) {
                              withTokenRefresh(() => apiClient.markSystemEventAsRead(ev.id)).catch(() => { /* ignore */ });
                              setSystemEvents((prev) =>
                                prev.map((e) => (e.id === ev.id ? { ...e, showUnread: false } : e))
                              );
                            }
                          }}
                        >
                          <div className="flex items-start gap-3">
                            {/* Icon */}
                            <img
                              src={getNotificationIcon(ev.event)}
                              alt=""
                              className="flex-shrink-0"
                              style={{ width: 48, height: 48 }}
                            />
                            {/* Content */}
                            <div className="flex-1 min-w-0 flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                {/* Title */}
                                <div className="text-[13px] font-semibold text-[#111827] capitalize truncate">
                                  {ev.context} · {ev.event}
                                </div>
                                {/* One-line description */}
                                {(() => {
                                  // Build a concise sentence based on context/event and jsonData
                                  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
                                  let nameOrBucket = '';
                                  try {
                                    if (ev.jsonData) {
                                      const obj = JSON.parse(ev.jsonData);
                                      nameOrBucket = obj.name || obj.bucket || '';
                                    }
                                  } catch {
                                    /* ignore */
                                  }
                                  const ctx = ev.context.toLowerCase();
                                  const evt = ev.event.toLowerCase();

                                  // Determine the action text
                                  let action = '';
                                  if (evt === 'created') action = 'successfully created';
                                  else if (evt === 'updated') action = ctx === 'usersettings' ? 'were changed' : 'updated';
                                  else if (evt === 'deleted') action = 'deleted';
                                  else action = evt;

                                  // Determine the type label
                                  let typeLabel = '';
                                  if (ctx === 'bucket') typeLabel = 'Bucket';
                                  else if (ctx === 'workspace') typeLabel = 'Workspace';
                                  else if (ctx === 'repo') typeLabel = 'Repository';
                                  else if (ctx === 'usersettings') typeLabel = 'User settings';
                                  else if (ctx === 'apikey') typeLabel = 'API Key';
                                  else typeLabel = cap(ctx);

                                  // Handle click to navigate to details page
                                  const handleItemClick = (e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    setShowNotificationsTab(false);
                                    setIsMobileMenuOpen(false);
                                    if (ctx === 'bucket') {
                                      navigate('/cloud-storage/buckets');
                                      handleTabChange({} as React.SyntheticEvent, TabIndex.StorageUnits);
                                    } else if (ctx === 'repo') {
                                      navigate('/repositories/inventory');
                                      handleTabChange({} as React.SyntheticEvent, TabIndex.StorageRepos);
                                    } else if (ctx === 'apikey') {
                                      navigate('/cloud-storage/overview');
                                      handleTabChange({} as React.SyntheticEvent, TabIndex.Dashboard);
                                    } else if (ctx === 'workspace') {
                                      navigate('/workspace/home');
                                      handleTabChange({} as React.SyntheticEvent, TabIndex.Home);
                                    } else if (ctx === 'organization') {
                                      navigate('/organization');
                                      handleTabChange({} as React.SyntheticEvent, TabIndex.Organization);
                                    } else if (ctx === 'usersettings' || ctx === 'user') {
                                      window.dispatchEvent(new CustomEvent('openProfileModal', { detail: { section: 'Profile' } }));
                                    }
                                  };

                                  return (
                                    <>
                                      <div className="text-[12px] text-[#6B7280] truncate mt-0.5">
                                        {nameOrBucket ? (
                                          <>
                                            {typeLabel}{' '}
                                            <span
                                              onClick={handleItemClick}
                                              style={{
                                                color: '#4766FF',
                                                cursor: 'pointer',
                                                fontWeight: 600,
                                              }}
                                              onMouseEnter={(e) =>
                                                (e.currentTarget.style.textDecoration = 'underline')
                                              }
                                              onMouseLeave={(e) =>
                                                (e.currentTarget.style.textDecoration = 'none')
                                              }
                                            >
                                              "{nameOrBucket}"
                                            </span>{' '}
                                            {action}
                                          </>
                                        ) : (
                                          `${typeLabel} ${action}`
                                        )}
                                      </div>
                                      {/* Show user email for user settings modifications */}
                                      {ctx === 'usersettings' && ev.userId && userEmailMap[ev.userId] && (
                                        <div className="text-[11px] text-[#9CA3AF] truncate mt-0.5">
                                          Modified by: {ev.userEmail}
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                              <div className="flex flex-col items-end gap-0.5 flex-none">
                                <div className="text-[12px] text-[#6B7280] whitespace-nowrap">
                                  {new Date(ev.timestamp).toLocaleString()}
                                </div>
                                {isOrgManager && ev.userId !== currentUserId && ev.userName && (
                                  <div className="text-[11px] text-[#9CA3AF] whitespace-nowrap">
                                    By: {ev.userName}
                                  </div>
                                )}
                                {!ev.showUnread && (
                                  <span className="text-[11px] text-[#22C55E] flex items-center gap-0.5">
                                    ✓ Read
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                  </ul>
                )}
              </div>
              {/* Footer: View All Notifications */}
              {isOrgManager && (
                <div className="shrink-0 border-t border-[#E9EAEB] pt-3 mt-2">
                  <button
                    type="button"
                    className="w-full text-center text-[13px] font-medium text-[#617BFF] hover:text-[#4F63CC] hover:underline py-1"
                    onClick={() => {
                      setShowNotificationsTab(false);
                      setIsMobileMenuOpen(false);
                      navigate('/activity');
                      handleTabChange({} as React.SyntheticEvent, TabIndex.Activity);
                    }}
                  >
                    View All Notifications
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>)}


      {/* Profile Modal */}
      <ProfileModal
        open={profileModalOpen}
        onClose={() => {
          setProfileModalOpen(false);
          setProfileModalInitialSection('Profile');
          setProfileModalInitialTab(undefined);
          setProfileModalHighlight2FA(false);
        }}
        initialSection={profileModalInitialSection}
        initialTab={profileModalInitialTab}
        highlight2FA={profileModalHighlight2FA}
      />
      {/* Organization Modal - standalone */}
      <OrganizationModal
        open={showOrganizationModal}
        onClose={() => {
          setShowOrganizationModal(false);
          setOrgModalInitialSection('Details');
          setOrgModalInitialShowHover(false);
        }}
        initialSection={orgModalInitialSection}
        initialShowHover={orgModalInitialShowHover}
      />

      {/* Create Workspace Dialog */}
      {createOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white w-[360px] rounded-xl border border-[#E9EAEB] p-4 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[16px] font-semibold text-[#101828]">Create workspace</span>
              <button className="text-gray-500" onClick={() => setCreateOpen(false)}>
                ✕
              </button>
            </div>
            {createError && <div className="text-sm text-red-500 mb-2">{createError}</div>}
            <div className="mb-3">
              <label className="block text-[12px] text-gray-500 mb-1">Workspace name</label>
              <input
                className="w-full h-10 border border-gray-200 rounded-lg px-2 text-[14px]"
                value={wsName}
                onChange={(e) => setWsName(e.target.value)}
                placeholder="My workspace"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                className="h-9 px-3 rounded-lg border border-[#E9EAEB] text-[#414651] text-[14px] font-semibold"
                onClick={() => setCreateOpen(false)}
                disabled={creating}
              >
                Cancel
              </button>
              <button
                className="h-9 px-3 rounded-lg bg-[#617BFF] text-white text-[14px] font-semibold disabled:opacity-60"
                onClick={handleCreateWorkspace}
                disabled={creating}
              >
                {creating ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cloud Storage Panel */}
      {showCloudStorageTab && (
        <div
          className="fixed z-40"
          style={{ left: cloudPanelPos.left, top: cloudPanelPos.top }}
          onMouseEnter={() => {
            cancelCloudHide();
            setShowCloudStorageTab(true);
          }}
          onMouseLeave={scheduleCloudHide}
        >
          <div className="w-[340px] bg-white border border-[#E9EAEB] rounded-xl p-4 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[14px] font-semibold text-[#101828]">Cloud Storage</span>
              <button
                className="text-gray-500"
                onClick={() => setShowCloudStorageTab(false)}
                title="Close"
              >
                ✕
              </button>
            </div>
            <div className="flex flex-col">
              <button
                className="w-full h-9 rounded-md text-left px-3 hover:bg-[#FAFAFA] text-[#344054] text-[14px] font-semibold"
                onClick={() => {
                  handleClick(-1, TabIndex.Dashboard);
                  setShowCloudStorageTab(false);
                }}
              >
                Overview
              </button>
              <button
                className="w-full h-9 rounded-md text-left px-3 hover:bg-[#FAFAFA] text-[#344054] text-[14px] font-semibold flex items-center gap-2"
                onClick={() => {
                  handleClick(-1, TabIndex.StorageUnits);
                  setShowCloudStorageTab(false);
                }}
              >
                <span>Buckets</span>
                <span className="ml-auto">{createCountBadge(bucketsCount)}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Network Panel */}
      {showNetworkTab && (
        <div
          className="fixed z-40"
          style={{ left: networkPanelPos.left, top: networkPanelPos.top }}
          onMouseEnter={() => {
            cancelNetworkHide();
            setShowNetworkTab(true);
          }}
          onMouseLeave={scheduleNetworkHide}
        >
          <div className="w-[340px] bg-white border border-[#E9EAEB] rounded-xl p-4 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[14px] font-semibold text-[#101828]">Network</span>
              <button
                className="text-gray-500"
                onClick={() => setShowNetworkTab(false)}
                title="Close"
              >
                ✕
              </button>
            </div>
            <div className="flex flex-col">
              <button
                className="w-full h-9 rounded-md text-left px-3 hover:bg-[#FAFAFA] text-[#344054] text-[14px] font-semibold"
                onClick={() => {
                  handleClick(-1, TabIndex.BridgeNodesPublic);
                  setShowNetworkTab(false);
                }}
              >
                Public Bridge Nodes
              </button>
              <button
                className="w-full h-9 rounded-md text-left px-3 hover:bg-[#FAFAFA] text-[#344054] text-[14px] font-semibold"
                onClick={() => {
                  handleClick(-1, TabIndex.BridgeNodesPrivate);
                  setShowNetworkTab(false);
                }}
              >
                Private Bridge Nodes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Repositories Panel */}
      {showRepositoriesTab && (
        <div
          className="fixed z-40"
          style={{ left: repositoriesPanelPos.left, top: repositoriesPanelPos.top }}
          onMouseEnter={() => {
            cancelRepositoriesHide();
            setShowRepositoriesTab(true);
          }}
          onMouseLeave={scheduleRepositoriesHide}
        >
          <div className="w-[340px] bg-white border border-[#E9EAEB] rounded-xl p-4 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[14px] font-semibold text-[#101828]">Repositories</span>
              <button
                className="text-gray-500"
                onClick={() => setShowRepositoriesTab(false)}
                title="Close"
              >
                ✕
              </button>
            </div>
            <div className="flex flex-col">
              <button
                className="w-full h-9 rounded-md text-left px-3 hover:bg-[#FAFAFA] text-[#344054] text-[14px] font-semibold"
                onClick={() => {
                  handleClick(-1, TabIndex.RepoOverview);
                  setShowRepositoriesTab(false);
                }}
              >
                Overview
              </button>
              <button
                className="w-full h-9 rounded-md text-left px-3 hover:bg-[#FAFAFA] text-[#344054] text-[14px] font-semibold flex items-center gap-2"
                data-tutorial="menu-inventory"
                onClick={() => {
                  handleClick(-1, TabIndex.StorageRepos);
                  setShowRepositoriesTab(false);
                }}
              >
                <span>Inventory</span>
                <span className="ml-auto">{createCountBadge(repositoriesCount)}</span>
              </button>
              <button
                className="w-full h-9 rounded-md text-left px-3 hover:bg-[#FAFAFA] text-[#344054] text-[14px] font-semibold"
                data-tutorial="menu-api-keys"
                onClick={() => {
                  handleClick(-1, TabIndex.APIKeys);
                  setShowRepositoriesTab(false);
                }}
              >
                API Keys
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ISP Market Panel */}
      {showDepinHubTab && (
        <div
          className="fixed z-40"
          style={{ left: depinHubPanelPos.left, top: depinHubPanelPos.top }}
          onMouseEnter={() => {
            cancelDepinHubHide();
            setShowDepinHubTab(true);
          }}
          onMouseLeave={scheduleDepinHubHide}
        >
          <div className="w-[340px] bg-white border border-[#E9EAEB] rounded-xl p-4 shadow-lg">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div style={{ width: 28, height: 28, borderRadius: 8, backgroundColor: '#F0F2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <DepinHubIcon size={15} color="#617BFF" />
                </div>
                <span className="text-[14px] font-semibold text-[#101828]">ISP Market</span>
              </div>
              <button
                className="text-gray-400 hover:text-gray-600 transition-colors"
                onClick={() => setShowDepinHubTab(false)}
                title="Close"
                style={{ fontSize: 14, lineHeight: 1 }}
              >
                ✕
              </button>
            </div>
            <div className="flex flex-col gap-0.5">
              {[
                {
                  label: 'Storage Units',
                  desc: 'Register and manage your buckets',
                  tab: TabIndex.StorageUnitsDePIN,
                  icon: (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <ellipse cx="12" cy="5" rx="9" ry="3" />
                      <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
                      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
                    </svg>
                  ),
                },
                {
                  label: 'Marketplace',
                  desc: 'Browse and purchase storage resources',
                  tab: TabIndex.Marketplace,
                  icon: (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                      <polyline points="9 22 9 12 15 12 15 22" />
                    </svg>
                  ),
                },
                {
                  label: 'Deals',
                  desc: 'Manage your active and past deals',
                  tab: TabIndex.MyDeals,
                  icon: (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                    </svg>
                  ),
                },
              ].map((item) => (
                <button
                  key={item.label}
                  className="w-full rounded-lg text-left px-3 py-2 hover:bg-[#F5F7FF] transition-colors group"
                  onClick={() => {
                    handleClick(-1, item.tab);
                    setShowDepinHubTab(false);
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div style={{ color: '#98A2B3', flexShrink: 0 }} className="group-hover:text-[#617BFF] transition-colors">
                      {item.icon}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#344054' }}>{item.label}</div>
                      <div style={{ fontSize: 11, color: '#98A2B3', lineHeight: '16px' }}>{item.desc}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* AI Panel */}
      {showAITab && (
        <div
          className="fixed z-40"
          style={{ left: aiPanelPos.left, top: aiPanelPos.top }}
          onMouseEnter={() => {
            cancelAIHide();
            setShowAITab(true);
          }}
          onMouseLeave={scheduleAIHide}
        >
          <div className="w-[340px] bg-white border border-[#E9EAEB] rounded-xl p-4 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[14px] font-semibold text-[#101828]">AI</span>
              <button
                className="text-gray-500"
                onClick={() => setShowAITab(false)}
                title="Close"
              >
                ✕
              </button>
            </div>
            <div className="flex flex-col">
              <button
                className="w-full h-9 rounded-md text-left px-3 hover:bg-[#FAFAFA] text-[#344054] text-[14px] font-semibold"
                onClick={() => {
                  handleClick(-1, TabIndex.AIOverview);
                  setShowAITab(false);
                }}
              >
                Overview
              </button>
              <button
                className="w-full h-9 rounded-md text-left px-3 hover:bg-[#FAFAFA] text-[#344054] text-[14px] font-semibold"
                data-tutorial="menu-llm"
                onClick={() => {
                  handleClick(-1, TabIndex.AILLM);
                  setShowAITab(false);
                }}
              >
                AI LLM
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Team Panel (Organization page) */}
      {showTeamTab && isOnOrganizationPage && (
        <div
          className="fixed z-40"
          style={{ left: teamPanelPos.left, top: teamPanelPos.top }}
          onMouseEnter={() => {
            cancelTeamHide();
            setShowTeamTab(true);
          }}
          onMouseLeave={scheduleTeamHide}
        >
          <div className="w-[280px] bg-white border border-[#E9EAEB] rounded-xl p-4 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[14px] font-semibold text-[#101828]">Team</span>
              <button
                className="text-gray-500"
                onClick={() => setShowTeamTab(false)}
                title="Close"
              >
                ✕
              </button>
            </div>
            <div className="flex flex-col">
              <button
                className="w-full h-9 rounded-md text-left px-3 hover:bg-[#FAFAFA] text-[#344054] text-[14px] font-semibold"
                onClick={() => {
                  navigate('/organization?tab=team&section=overview');
                  setShowTeamTab(false);
                }}
              >
                Overview
              </button>
              <button
                className="w-full h-9 rounded-md text-left px-3 hover:bg-[#FAFAFA] text-[#344054] text-[14px] font-semibold"
                onClick={() => {
                  navigate('/organization?tab=team&section=members');
                  setShowTeamTab(false);
                }}
              >
                Members
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Workspace Panel (Organization page) */}
      {showWorkspaceTab && isOnOrganizationPage && (
        <div
          className="fixed z-40"
          style={{ left: workspacePanelPos.left, top: workspacePanelPos.top }}
          onMouseEnter={() => {
            cancelWorkspaceHide();
            setShowWorkspaceTab(true);
          }}
          onMouseLeave={scheduleWorkspaceHide}
        >
          <div className="w-[280px] bg-white border border-[#E9EAEB] rounded-xl p-4 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[14px] font-semibold text-[#101828]">Workspace</span>
              <button
                className="text-gray-500"
                onClick={() => setShowWorkspaceTab(false)}
                title="Close"
              >
                ✕
              </button>
            </div>
            <div className="flex flex-col">
              <button
                className="w-full h-9 rounded-md text-left px-3 hover:bg-[#FAFAFA] text-[#344054] text-[14px] font-semibold"
                onClick={() => {
                  navigate('/organization?tab=workspace&section=overview');
                  setShowWorkspaceTab(false);
                }}
              >
                Overview
              </button>
              <button
                className="w-full h-9 rounded-md text-left px-3 hover:bg-[#FAFAFA] text-[#344054] text-[14px] font-semibold"
                onClick={() => {
                  navigate('/organization?tab=workspace&section=management');
                  setShowWorkspaceTab(false);
                }}
              >
                Management
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Policies Panel (Organization page) */}
      {showPoliciesTab && isOnOrganizationPage && (
        <div
          className="fixed z-40"
          style={{ left: policiesPanelPos.left, top: policiesPanelPos.top }}
          onMouseEnter={() => {
            cancelPoliciesHide();
            setShowPoliciesTab(true);
          }}
          onMouseLeave={schedulePoliciesHide}
        >
          <div className="w-[280px] bg-white border border-[#E9EAEB] rounded-xl p-4 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[14px] font-semibold text-[#101828]">Policies</span>
              <button
                className="text-gray-500"
                onClick={() => setShowPoliciesTab(false)}
                title="Close"
              >
                ✕
              </button>
            </div>
            <div className="flex flex-col">
              <button
                className="w-full h-9 rounded-md text-left px-3 hover:bg-[#FAFAFA] text-[#344054] text-[14px] font-semibold"
                onClick={() => {
                  navigate('/organization?tab=policies&section=llm-guardrails');
                  setShowPoliciesTab(false);
                }}
              >
                LLM Guardrails
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Statistics Panel (Organization page) */}
      {showStatisticsTab && isOnOrganizationPage && (
        <div
          className="fixed z-40"
          style={{ left: statisticsPanelPos.left, top: statisticsPanelPos.top }}
          onMouseEnter={() => {
            cancelStatisticsHide();
            setShowStatisticsTab(true);
          }}
          onMouseLeave={scheduleStatisticsHide}
        >
          <div className="w-[280px] bg-white border border-[#E9EAEB] rounded-xl p-4 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[14px] font-semibold text-[#101828]">Statistics</span>
              <button
                className="text-gray-500"
                onClick={() => setShowStatisticsTab(false)}
                title="Close"
              >
                ✕
              </button>
            </div>
            <div className="flex flex-col">
              <button
                className="w-full h-9 rounded-md text-left px-3 hover:bg-[#FAFAFA] text-[#344054] text-[14px] font-semibold"
                onClick={() => {
                  navigate('/organization?tab=statistics&section=gateway');
                  setShowStatisticsTab(false);
                }}
              >
                Gateway
              </button>
              <button
                className="w-full h-9 rounded-md text-left px-3 hover:bg-[#FAFAFA] text-[#344054] text-[14px] font-semibold flex items-center gap-2"
                onClick={() => {
                  navigate('/organization?tab=statistics&section=chat');
                  setShowStatisticsTab(false);
                }}
              >
                Private Chat
              </button>
              <button
                className="w-full h-9 rounded-md text-left px-3 hover:bg-[#FAFAFA] text-[#344054] text-[14px] font-semibold"
                onClick={() => {
                  navigate('/organization?tab=statistics&section=policies');
                  setShowStatisticsTab(false);
                }}
              >
                Policies
              </button>
              <button
                className="w-full h-9 rounded-md text-left px-3 hover:bg-[#FAFAFA] text-[#344054] text-[14px] font-semibold"
                onClick={() => {
                  navigate('/organization?tab=statistics&section=credits');
                  setShowStatisticsTab(false);
                }}
              >
                Credits
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Skip Tutorial Confirmation Dialog */}
      <Dialog
        open={showSkipConfirmDialog}
        onClose={() => setShowSkipConfirmDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow:
              '0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: '18px',
            lineHeight: '28px',
            color: '#101828',
            px: 3,
            pt: 3,
            pb: 2,
          }}
        >
          Skip Tutorial?
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 3 }}>
          <Typography sx={{ color: '#475467', fontSize: '14px', lineHeight: '20px' }}>
            Are you sure you want to ignore the tutorial? You can always restart it later from your
            account settings.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, gap: 1.5 }}>
          <Button
            onClick={() => setShowSkipConfirmDialog(false)}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '20px',
              borderColor: '#E9EAEB',
              color: '#344054',
              px: 2,
              py: 1,
              borderRadius: '8px',
              '&:hover': {
                borderColor: '#D1D5DB',
                bgcolor: '#F9FAFB',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              try {
                localStorage.setItem('hideOnboardingTutorial', 'true');
              } catch (error) { /* Ignore errors */ }
              setHideTutorial(true);
              setShowAccountSettingsTab(false);
              setShowSkipConfirmDialog(false);
            }}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '20px',
              bgcolor: '#617BFF',
              px: 2.5,
              py: 1,
              borderRadius: '8px',
              boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
              '&:hover': {
                bgcolor: '#5A6FE8',
                boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
              },
            }}
          >
            Yes, Skip
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upgrade Plan Modal */}
      <UpgradePlanModal
        open={upgradePlanModalOpen}
        onClose={() => setUpgradePlanModalOpen(false)}
      />
    </div>
  );
};

export default Sidebar;
